// app/frontend/pages/Billing/Billing.tsx
import { useState, useEffect } from "react";
import {
  Box,
  Container,
  Stack,
  Group,
  SimpleGrid,
  Divider,
  Modal,
  Table,
  Badge,
  Skeleton,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import {
  IconCreditCard,
  IconReceipt,
  IconCalendar,
  IconCheck,
  IconAlertCircle,
  IconExternalLink,
  IconArrowsExchange,
} from "@tabler/icons-react";
import { loadStripe, Stripe } from "@stripe/stripe-js";
import {
  Elements,
  CardElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import { L2KH1, L2KH2, L2KH3, L2KText } from "../../components/ui/Typography";
import { L2KCard } from "../../components/ui/Cards";
import { L2KButton } from "../../components/ui/Forms";
import AuthenticatedLayout from "../../components/layouts/AuthenticatedLayout";
import { SEO } from "../../components/common/SEO";

interface Plan {
  id: string;
  name: string;
  price: number;
  price_formatted: string;
  interval: string;
}

interface PaymentMethod {
  id: string;
  brand: string;
  last4: string;
  exp_month: number;
  exp_year: number;
}

interface Subscription {
  id: string;
  status: string;
  cancel_at_period_end: boolean;
  current_period_start: number;
  current_period_end: number;
  created: number;
  plan: string | null;
  price_formatted: string | null;
}

interface Invoice {
  id: string;
  number: string;
  amount_due: number;
  amount_paid: number;
  currency: string;
  status: string;
  created: number;
  hosted_invoice_url: string;
  invoice_pdf: string;
}

interface BillingPageProps {
  user: {
    id: number;
    email: string;
    name: string;
    stripe_customer_id: string | null;
    subscription_status: string;
    subscription_plan: string | null;
    subscription_active: boolean;
  };
  subscription: Subscription | null;
  payment_method: PaymentMethod | null;
  invoices: Invoice[];
  plans: {
    monthly: Plan;
    annual: Plan;
  };
  stripe_publishable_key: string;
}

let stripePromise: Promise<Stripe | null> | null = null;

export default function Billing({
  user,
  subscription,
  payment_method,
  invoices,
  plans,
  stripe_publishable_key,
}: BillingPageProps) {
  // Initialize Stripe
  if (!stripePromise && stripe_publishable_key) {
    stripePromise = loadStripe(stripe_publishable_key);
  }

  const [paymentMethodModalOpened, { open: openPaymentModal, close: closePaymentModal }] =
    useDisclosure(false);
  const [changePlanModalOpened, { open: openPlanModal, close: closePlanModal }] =
    useDisclosure(false);

  return (
    <AuthenticatedLayout>
      <SEO title="Billing - Learntokiz" />

      <Container size="lg" py="xl">
        <Stack gap="xl">
          {/* Header */}
          <Stack gap="xs">
            <L2KH1>Billing & Subscription</L2KH1>
            <L2KText variant="secondary">
              Manage your subscription, payment method, and view invoices
            </L2KText>
          </Stack>

          <SimpleGrid cols={{ base: 1, md: 2 }} spacing="lg">
            {/* Current Plan Card */}
            <CurrentPlanCard
              subscription={subscription}
              plans={plans}
              onChangePlan={openPlanModal}
            />

            {/* Payment Method Card */}
            <PaymentMethodCard
              paymentMethod={payment_method}
              onUpdate={openPaymentModal}
            />
          </SimpleGrid>

          {/* Invoices Section */}
          <InvoicesSection invoices={invoices} />
        </Stack>
      </Container>

      {/* Update Payment Method Modal */}
      <Modal
        opened={paymentMethodModalOpened}
        onClose={closePaymentModal}
        title={<L2KH3>Update Payment Method</L2KH3>}
        centered
        styles={{
          content: {
            backgroundColor: "#1a1a1a",
            border: "1px solid rgba(255, 152, 0, 0.2)",
          },
          header: {
            backgroundColor: "#1a1a1a",
          },
        }}
      >
        {stripePromise && (
          <Elements stripe={stripePromise}>
            <UpdatePaymentMethodForm onSuccess={closePaymentModal} />
          </Elements>
        )}
      </Modal>

      {/* Change Plan Modal */}
      <Modal
        opened={changePlanModalOpened}
        onClose={closePlanModal}
        title={<L2KH3>Change Plan</L2KH3>}
        centered
        styles={{
          content: {
            backgroundColor: "#1a1a1a",
            border: "1px solid rgba(255, 152, 0, 0.2)",
          },
          header: {
            backgroundColor: "#1a1a1a",
          },
        }}
      >
        <ChangePlanForm
          currentPlan={subscription?.plan || null}
          plans={plans}
          onSuccess={closePlanModal}
        />
      </Modal>
    </AuthenticatedLayout>
  );
}

// Current Plan Card Component
function CurrentPlanCard({
  subscription,
  plans,
  onChangePlan,
}: {
  subscription: Subscription | null;
  plans: { monthly: Plan; annual: Plan };
  onChangePlan: () => void;
}) {
  const currentPlan = subscription?.plan
    ? plans[subscription.plan as keyof typeof plans]
    : null;

  const formatDate = (timestamp: number) => {
    return new Date(timestamp * 1000).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const getStatusBadge = (status: string, cancelAtPeriodEnd: boolean) => {
    if (cancelAtPeriodEnd) {
      return <Badge color="yellow">Canceling</Badge>;
    }
    switch (status) {
      case "active":
        return <Badge color="green">Active</Badge>;
      case "trialing":
        return <Badge color="blue">Trial</Badge>;
      case "past_due":
        return <Badge color="red">Past Due</Badge>;
      case "canceled":
        return <Badge color="gray">Canceled</Badge>;
      default:
        return <Badge color="gray">{status}</Badge>;
    }
  };

  return (
    <L2KCard variant="elevated">
      <Stack gap="md">
        <Group justify="space-between">
          <Group gap="sm">
            <IconCalendar size={20} color="#FF9800" />
            <L2KH3>Current Plan</L2KH3>
          </Group>
          {subscription && getStatusBadge(subscription.status, subscription.cancel_at_period_end)}
        </Group>

        <Divider color="rgba(255, 255, 255, 0.1)" />

        {subscription && currentPlan ? (
          <Stack gap="sm">
            <Group justify="space-between">
              <L2KText variant="secondary">Plan</L2KText>
              <L2KText fw={600}>{currentPlan.name}</L2KText>
            </Group>
            <Group justify="space-between">
              <L2KText variant="secondary">Price</L2KText>
              <L2KText fw={600}>{subscription.price_formatted}</L2KText>
            </Group>
            <Group justify="space-between">
              <L2KText variant="secondary">
                {subscription.cancel_at_period_end ? "Access until" : "Next billing date"}
              </L2KText>
              <L2KText fw={600}>{formatDate(subscription.current_period_end)}</L2KText>
            </Group>

            {subscription.cancel_at_period_end && (
              <Box
                p="sm"
                style={{
                  backgroundColor: "rgba(255, 193, 7, 0.1)",
                  borderRadius: "8px",
                  border: "1px solid rgba(255, 193, 7, 0.3)",
                }}
              >
                <L2KText size="sm" ta="center">
                  Your subscription will end on {formatDate(subscription.current_period_end)}
                </L2KText>
              </Box>
            )}

            <Group gap="sm" mt="sm">
              <L2KButton
                variant="outline"
                leftSection={<IconArrowsExchange size={18} />}
                onClick={onChangePlan}
                style={{ flex: 1 }}
              >
                Change Plan
              </L2KButton>
              {!subscription.cancel_at_period_end && (
                <L2KButton
                  variant="subtle"
                  component="a"
                  href="/billing/cancel"
                  style={{ color: "#888" }}
                >
                  Cancel
                </L2KButton>
              )}
            </Group>
          </Stack>
        ) : (
          <Stack gap="sm" align="center" py="md">
            <L2KText variant="secondary">No active subscription</L2KText>
            <L2KButton component="a" href="/pricing">
              View Plans
            </L2KButton>
          </Stack>
        )}
      </Stack>
    </L2KCard>
  );
}

// Payment Method Card Component
function PaymentMethodCard({
  paymentMethod,
  onUpdate,
}: {
  paymentMethod: PaymentMethod | null;
  onUpdate: () => void;
}) {
  return (
    <L2KCard variant="elevated">
      <Stack gap="md">
        <Group gap="sm">
          <IconCreditCard size={20} color="#FF9800" />
          <L2KH3>Payment Method</L2KH3>
        </Group>

        <Divider color="rgba(255, 255, 255, 0.1)" />

        {paymentMethod ? (
          <Stack gap="sm">
            <Group justify="space-between">
              <L2KText variant="secondary">Card</L2KText>
              <L2KText fw={600}>
                {paymentMethod.brand} **** {paymentMethod.last4}
              </L2KText>
            </Group>
            <Group justify="space-between">
              <L2KText variant="secondary">Expires</L2KText>
              <L2KText fw={600}>
                {paymentMethod.exp_month.toString().padStart(2, "0")}/{paymentMethod.exp_year}
              </L2KText>
            </Group>

            <L2KButton variant="outline" onClick={onUpdate} mt="sm">
              Update Payment Method
            </L2KButton>
          </Stack>
        ) : (
          <Stack gap="sm" align="center" py="md">
            <L2KText variant="secondary">No payment method on file</L2KText>
            <L2KButton onClick={onUpdate}>Add Payment Method</L2KButton>
          </Stack>
        )}
      </Stack>
    </L2KCard>
  );
}

// Update Payment Method Form
function UpdatePaymentMethodForm({ onSuccess }: { onSuccess: () => void }) {
  const stripe = useStripe();
  const elements = useElements();
  const [isLoading, setIsLoading] = useState(false);
  const [clientSecret, setClientSecret] = useState<string | null>(null);

  useEffect(() => {
    // Get SetupIntent client secret
    const fetchSetupIntent = async () => {
      const csrfToken = document.querySelector<HTMLMetaElement>(
        'meta[name="csrf-token"]'
      )?.content;

      const response = await fetch("/billing/setup_intent", {
        method: "POST",
        headers: {
          Accept: "application/json",
          "X-CSRF-Token": csrfToken || "",
        },
        credentials: "include",
      });

      const data = await response.json();
      if (data.client_secret) {
        setClientSecret(data.client_secret);
      }
    };

    fetchSetupIntent();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!stripe || !elements || !clientSecret) return;

    setIsLoading(true);

    const cardElement = elements.getElement(CardElement);
    if (!cardElement) return;

    const { error, setupIntent } = await stripe.confirmCardSetup(clientSecret, {
      payment_method: {
        card: cardElement,
      },
    });

    if (error) {
      notifications.show({
        color: "red",
        title: "Error",
        message: error.message || "Failed to update payment method",
        icon: <IconAlertCircle />,
      });
      setIsLoading(false);
      return;
    }

    if (setupIntent?.payment_method) {
      // Save to backend
      const csrfToken = document.querySelector<HTMLMetaElement>(
        'meta[name="csrf-token"]'
      )?.content;

      const response = await fetch("/billing/payment_method", {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-CSRF-Token": csrfToken || "",
        },
        credentials: "include",
        body: JSON.stringify({ payment_method_id: setupIntent.payment_method }),
      });

      if (response.ok) {
        notifications.show({
          color: "green",
          title: "Success",
          message: "Payment method updated",
          icon: <IconCheck />,
        });
        onSuccess();
        window.location.reload();
      } else {
        const data = await response.json();
        notifications.show({
          color: "red",
          title: "Error",
          message: data.error || "Failed to save payment method",
          icon: <IconAlertCircle />,
        });
      }
    }

    setIsLoading(false);
  };

  return (
    <form onSubmit={handleSubmit}>
      <Stack gap="md">
        <Box
          p="md"
          style={{
            backgroundColor: "rgba(255, 255, 255, 0.05)",
            borderRadius: "8px",
            border: "1px solid rgba(255, 152, 0, 0.2)",
          }}
        >
          {clientSecret ? (
            <CardElement
              options={{
                style: {
                  base: {
                    fontSize: "16px",
                    color: "#F0F0F0",
                    fontFamily: "'Public Sans', sans-serif",
                    "::placeholder": {
                      color: "#888",
                    },
                  },
                  invalid: {
                    color: "#ff6b6b",
                  },
                },
              }}
            />
          ) : (
            <Skeleton height={24} />
          )}
        </Box>

        <Group justify="flex-end" gap="sm">
          <L2KButton variant="subtle" onClick={onSuccess} disabled={isLoading}>
            Cancel
          </L2KButton>
          <L2KButton type="submit" loading={isLoading} disabled={!clientSecret}>
            Update Card
          </L2KButton>
        </Group>
      </Stack>
    </form>
  );
}

// Change Plan Form
function ChangePlanForm({
  currentPlan,
  plans,
  onSuccess,
}: {
  currentPlan: string | null;
  plans: { monthly: Plan; annual: Plan };
  onSuccess: () => void;
}) {
  const [isLoading, setIsLoading] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<string | null>(null);

  const handleChangePlan = async () => {
    if (!selectedPlan || selectedPlan === currentPlan) return;

    setIsLoading(true);

    const csrfToken = document.querySelector<HTMLMetaElement>(
      'meta[name="csrf-token"]'
    )?.content;

    try {
      const response = await fetch("/billing/plan", {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-CSRF-Token": csrfToken || "",
        },
        credentials: "include",
        body: JSON.stringify({ plan: selectedPlan }),
      });

      if (response.ok) {
        notifications.show({
          color: "green",
          title: "Plan Changed",
          message: `You've switched to the ${plans[selectedPlan as keyof typeof plans].name} plan`,
          icon: <IconCheck />,
        });
        onSuccess();
        window.location.reload();
      } else {
        const data = await response.json();
        notifications.show({
          color: "red",
          title: "Error",
          message: data.error || "Failed to change plan",
          icon: <IconAlertCircle />,
        });
      }
    } catch {
      notifications.show({
        color: "red",
        title: "Error",
        message: "Something went wrong",
        icon: <IconAlertCircle />,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Stack gap="md">
      {Object.values(plans).map((plan) => (
        <Box
          key={plan.id}
          onClick={() => setSelectedPlan(plan.id)}
          style={{
            padding: "16px",
            borderRadius: "8px",
            border: `2px solid ${
              selectedPlan === plan.id
                ? "#FF9800"
                : plan.id === currentPlan
                ? "rgba(34, 197, 94, 0.5)"
                : "rgba(255, 152, 0, 0.2)"
            }`,
            backgroundColor:
              selectedPlan === plan.id
                ? "rgba(255, 152, 0, 0.1)"
                : "transparent",
            cursor: plan.id === currentPlan ? "default" : "pointer",
            opacity: plan.id === currentPlan ? 0.6 : 1,
          }}
        >
          <Group justify="space-between">
            <Stack gap={4}>
              <Group gap="sm">
                <L2KText fw={600}>{plan.name}</L2KText>
                {plan.id === currentPlan && (
                  <Badge color="green" size="sm">
                    Current
                  </Badge>
                )}
              </Group>
              <L2KText variant="secondary" size="sm">
                {plan.price_formatted}
              </L2KText>
            </Stack>
          </Group>
        </Box>
      ))}

      <Group justify="flex-end" gap="sm" mt="md">
        <L2KButton variant="subtle" onClick={onSuccess} disabled={isLoading}>
          Cancel
        </L2KButton>
        <L2KButton
          onClick={handleChangePlan}
          loading={isLoading}
          disabled={!selectedPlan || selectedPlan === currentPlan}
        >
          Confirm Change
        </L2KButton>
      </Group>

      <L2KText size="xs" variant="tertiary" ta="center">
        Plan changes are prorated. You'll be charged or credited the difference.
      </L2KText>
    </Stack>
  );
}

// Invoices Section
function InvoicesSection({ invoices }: { invoices: Invoice[] }) {
  const formatDate = (timestamp: number) => {
    return new Date(timestamp * 1000).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const formatAmount = (amount: number, currency: string) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency.toUpperCase(),
    }).format(amount / 100);
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "paid":
        return <Badge color="green">Paid</Badge>;
      case "open":
        return <Badge color="yellow">Open</Badge>;
      case "draft":
        return <Badge color="gray">Draft</Badge>;
      case "uncollectible":
        return <Badge color="red">Failed</Badge>;
      default:
        return <Badge color="gray">{status}</Badge>;
    }
  };

  return (
    <L2KCard variant="elevated">
      <Stack gap="md">
        <Group gap="sm">
          <IconReceipt size={20} color="#FF9800" />
          <L2KH3>Invoice History</L2KH3>
        </Group>

        <Divider color="rgba(255, 255, 255, 0.1)" />

        {invoices.length > 0 ? (
          <Table
            highlightOnHover
            styles={{
              table: {
                backgroundColor: "transparent",
              },
              th: {
                color: "#B8B8B8",
                fontFamily: "'Public Sans', sans-serif",
                fontWeight: 500,
                borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
              },
              td: {
                color: "#F0F0F0",
                fontFamily: "'Public Sans', sans-serif",
                borderBottom: "1px solid rgba(255, 255, 255, 0.05)",
              },
              tr: {
                "&:hover": {
                  backgroundColor: "rgba(255, 152, 0, 0.05)",
                },
              },
            }}
          >
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Date</Table.Th>
                <Table.Th>Invoice</Table.Th>
                <Table.Th>Amount</Table.Th>
                <Table.Th>Status</Table.Th>
                <Table.Th></Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {invoices.map((invoice) => (
                <Table.Tr key={invoice.id}>
                  <Table.Td>{formatDate(invoice.created)}</Table.Td>
                  <Table.Td>{invoice.number || "-"}</Table.Td>
                  <Table.Td>{formatAmount(invoice.amount_paid || invoice.amount_due, invoice.currency)}</Table.Td>
                  <Table.Td>{getStatusBadge(invoice.status)}</Table.Td>
                  <Table.Td>
                    {invoice.hosted_invoice_url && (
                      <L2KButton
                        variant="subtle"
                        size="xs"
                        component="a"
                        href={invoice.hosted_invoice_url}
                        target="_blank"
                        rightSection={<IconExternalLink size={14} />}
                      >
                        View
                      </L2KButton>
                    )}
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        ) : (
          <L2KText variant="secondary" ta="center" py="md">
            No invoices yet
          </L2KText>
        )}
      </Stack>
    </L2KCard>
  );
}
